import { Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  withEntities,
  selectAllEntities,
  setEntities,
  getEntity,
  addEntities,
  updateEntities,
  deleteEntities,
} from '@ngneat/elf-entities';
import { withPagination } from '@ngneat/elf-pagination';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import {
  IStoreProps,
  IListParams,
  IPaginationState,
  IScrollPosition,
} from 'src/app/talent-litmus-core/common/common.interface';
import { IClassificationGame } from './classification-game.interface';
import { Observable, map, tap } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl3-classification-game';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IClassificationGame, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
  withPagination(),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class ClassificationGameCoreService {
  private _loading = false;

  /**
   * @name classificationGameList$
   * @description emits stored list of the companies
   */

  classificationGameList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 400);
    } else {
      this.refresh();
    }
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public getClassificationGames(params?: IListParams): void {
    this._loading = true;
    this._api
      .get(`classification-game/list`, params || {})
      .subscribe((data: IClassificationGameListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),
          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  public addClassificationGame(dataInput): Observable<IClassificationGame> {
    this._loading = true;
    return this._api.post('classification-game/add', dataInput || {}).pipe(
      tap((data: IClassificationGame) => {
        store.update(addEntities(data));
        this._loading = false;
      }),
    );
  }

  public updateClassificationGame(dataInput): Observable<IClassificationGame> {
    this._loading = true;
    return this._api.put('classification-game/edit', dataInput || {}).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: IClassificationGame) => {
        store.update(updateEntities(data['_id'], data));
        this._loading = false;
      }),
    );
  }

  public deleteClassificationGame(dataInput): Observable<any> {
    this._loading = true;
    return this._api.delete('classification-game', { body: dataInput }).pipe(
      tap(() => {
        store.update(deleteEntities(dataInput['_id']));
        this._loading = false;
      }),
    );
  }

  public getClassificationGame(
    gameId: string,
  ): Observable<IClassificationGame> {
    return this._api.get(`classification-game/${gameId}`);
  }

  public getClassificationGameInfo(id: string): IClassificationGame {
    const classificationGame = store.query(getEntity(id));
    return classificationGame;
  }

  public resolveClassificationGameId(
    classificationGame: string | IClassificationGame,
  ): string {
    let classificationGameId: string | IClassificationGame = '';
    if (classificationGame) {
      classificationGameId = classificationGame;
    }
    if (classificationGame['_id']) {
      classificationGameId = classificationGame['_id'];
    }
    return classificationGameId as string;
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getClassificationGames();
    }
  }
}

interface IClassificationGameListResponse {
  list: IClassificationGame[];
  totalCount: number;
}
